// hooks/useRoles.ts
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment";
import { useAuthToken } from "../data/redux/useAuthToken";

const ROLE_BASE_URL = `${API_BASE_PATH}/role/`;

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

export const getRolesList = async (_token: string | null) => {
  try {
    const res = await axios.get(ROLE_BASE_URL, {
      headers: { authorization: "Bearer " + _token },
    });
    return res.data;
  } catch (error) {
    const msg = extractErrorMessage(error);
    toast.error(msg);
    return [];
  }
};

export const useRoles = () => {
  const [roles, setRoles] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const empToken = useAuthToken();

  const fetchRoles = async (_token: string | null) => {
    setLoading(true);
    try {
      const res = await axios.get(ROLE_BASE_URL, {
        headers: { authorization: "Bearer " + _token },
      });
      setRoles(res.data);
    } catch (error) {
      const msg = extractErrorMessage(error);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles(empToken);
  }, []);

  const addRole = async (
    _token: string | null,
    role: { role_name: string; status: string }
  ) => {
    try {
      await axios.post(ROLE_BASE_URL, role, {
        headers: { authorization: "Bearer " + _token },
      });
      toast.success("Role added successfully");
      await fetchRoles(empToken);
    } catch (error) {
      const msg = extractErrorMessage(error);
      toast.error(msg);
    }
  };

  const updateRole = async (
    _token: string | null,
    role_name: string,
    payload: { status: string }
  ) => {
    try {
      const encodedName = encodeURIComponent(role_name);
      const body = {
        role_name,
        status: payload.status,
      };
      await axios.put(`${ROLE_BASE_URL}${encodedName}`, body, {
        headers: { authorization: "Bearer " + _token },
      });
      toast.success("Role updated successfully");
      await fetchRoles(empToken);
    } catch (error) {
      const msg = extractErrorMessage(error);
      toast.error(msg);
    }
  };

  const deleteRole = async (_token: string | null, role_name: string) => {
    try {
      const encodedName = encodeURIComponent(role_name);
      const response = await axios.delete(`${ROLE_BASE_URL}${encodedName}`, {
        headers: { authorization: "Bearer " + _token },
      });

      const apiStatus = response?.data?.status;
      const message = response?.data?.message || "Something went wrong";

      if (apiStatus?.includes("423")) {
        toast.warning("Role Associated with Some Data");
      } else if (response.status === 200) {
        toast.success("Role deleted successfully");
      }

      await fetchRoles(empToken);
      return response.data;
    } catch (error: any) {
      if (error?.status === 423) {
        toast.warning("Role Associated with Some Data");
      } else {
        const msg = extractErrorMessage(error);
        toast.error(msg);
      }
    }
  };

  return {
    roles,
    isLoading,
    addRole,
    updateRole,
    deleteRole,
    fetchRoles,
  };
};
